import { BUSINESS_INACTIVE_ERROR, DEVICE_ERROR_CODES } from '@iws/backoffice-api'

import { apiHeaders } from '@/clients/http'
import { configService } from '@/services/ConfigService'

/** The token is missing or expired: sync waits for a password sign-in. */
export class AuthExpiredError extends Error {
  constructor() {
    super('Sign in to sync.')
  }
}

/** The server refused this device: it was never linked, or was unlinked in the backoffice (POS.md O2). */
export class DeviceUnlinkedError extends Error {
  constructor(message: string) {
    super(message)
  }
}

export class BusinessInactiveError extends Error {
  constructor(message?: string) {
    super(message || 'This business is suspended. Sales are kept on this device. Contact support.')
  }
}

/** The server is down for maintenance (503): sync waits and retries, the till keeps selling (§15.8 U1, U3). */
export class MaintenanceError extends Error {
  constructor() {
    super('The server is under maintenance. Sales are kept on this device and sync when it is back.')
  }
}

export interface StreamEvent {
  name: string
  data: any
}

/**
 * Calls a `/pos-offline` endpoint and hands each server-sent event to `onEvent` as it arrives. EventSource cannot
 * send headers or a body, so this reads the response stream itself. Resolves when the server closes the stream.
 */
export const streamRequest = async (
  path: string,
  init: {
    method: 'GET' | 'POST'
    query?: Record<string, string | undefined>
    body?: unknown
  },
  onEvent: (event: StreamEvent) => Promise<void> | void
): Promise<void> => {
  const { baseUrl } = configService.getConfig().apiConfig
  const url = new URL(`${baseUrl}${path}`)
  Object.entries(init.query ?? {}).forEach(([key, value]) => value !== undefined && url.searchParams.set(key, value))

  const response = await fetch(url, {
    method: init.method,
    headers: {
      ...apiHeaders(),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: init.body ? JSON.stringify(init.body) : undefined,
  })
  if (response.status === 401) throw new AuthExpiredError()
  if (response.status === 503) throw new MaintenanceError()
  if (response.status === 403) {
    const body = await response.json().catch(() => null)
    if (DEVICE_ERROR_CODES.includes(body?.code)) throw new DeviceUnlinkedError(body.message)
    if (body?.code === BUSINESS_INACTIVE_ERROR) throw new BusinessInactiveError(body.message)
  }
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '')
    throw new Error(`${path} failed (${response.status}) ${text}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let end: number
    while ((end = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, end)
      buffer = buffer.slice(end + 2)
      let name = 'message'
      let data = ''
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) name = line.slice(7)
        else if (line.startsWith('data: ')) data += line.slice(6)
      }
      await onEvent({ name, data: data ? JSON.parse(data) : null })
    }
  }
}
